'use client';

import { useState, useTransition, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { Minus, Plus, ShoppingBag, Trash2, CreditCard, X, Bitcoin, ChevronDown, ChevronUp, Truck, PackageCheck, Wallet, UserCircle } from 'lucide-react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useCartStore } from '@/lib/store/cart-store';
import { useFrontSession } from '@/components/front-session-provider';
import { getCriticalZIndex, getNotificationZIndex } from '@/lib/utils/z-index-utils';
import {
  initiateBtcpayCheckout,
  initiatePagaditoCheckout,
  type CheckoutCartLine,
} from '@/app/actions/checkout-actions';
import { pagaditoConfig } from '@/lib/payments/pagadito-config';
import { Label } from '@/components/ui/label';

const BTCPAY_ENABLED = process.env.NEXT_PUBLIC_ENABLE_BTCPAY_CHECKOUT === 'true';
interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatPrice(value: number): string {
  return `$${value.toFixed(2)}`;
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const session = useFrontSession();
  
  const [isPending, startTransition] = useTransition();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [wantsShipping, setWantsShipping] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'pagadito' | 'bitcoin'>('pagadito');
  const items = useCartStore((state) => state.items);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const total = useCartStore((state) => state.getTotalPrice());
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [isProductsExpanded, setIsProductsExpanded] = useState(false);
  const [isPaymentExpanded, setIsPaymentExpanded] = useState(false);
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);

  // Load persistence
  useEffect(() => {
    const saved = localStorage.getItem('cart-drawer-state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (typeof parsed.products === 'boolean') setIsProductsExpanded(parsed.products);
        if (typeof parsed.payment === 'boolean') setIsPaymentExpanded(parsed.payment);
        if (typeof parsed.shipping === 'boolean') setIsShippingExpanded(parsed.shipping);
      } catch (e) {
        console.error('Failed to parse cart drawer state', e);
      }
    }
  }, []);

  // Save persistence
  useEffect(() => {
    localStorage.setItem('cart-drawer-state', JSON.stringify({
      products: isProductsExpanded,
      payment: isPaymentExpanded,
      shipping: isShippingExpanded,
    }));
  }, [isProductsExpanded, isPaymentExpanded, isShippingExpanded]);
  const shippingCost = total > 0 && wantsShipping ? pagaditoConfig.shippingFixed : 0;
  const transactionFee = pagaditoConfig.enableFeeFixed
    ? total > 0
      ? Number(pagaditoConfig.feeFixed.toFixed(2))
      : 0
    : 0;
  const cardProcessingFee = total > 0 && pagaditoConfig.enableFeePercent
    ? Number((total * pagaditoConfig.feePercent * (pagaditoConfig.enableIva ? 1 + pagaditoConfig.ivaPercent : 1)).toFixed(2))
    : 0;
  const finalTotal = total + shippingCost + transactionFee + cardProcessingFee;

  const handleCheckout = () => {
    setCheckoutError(null);
    if (items.length === 0) return;

    if (!session.authenticated) {
      const currentUrl = pathname + (searchParams.toString() ? '?' + searchParams.toString() : '');
      router.push(`/auth/login?next=${encodeURIComponent(currentUrl)}`);
      onClose();
      return;
    }

    const lines: CheckoutCartLine[] = items.map((item) => ({
      productId: item.productId,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
    }));

    startTransition(() => {
      void (async () => {
        // If cart changed and we have an old order, we should ideally cancel it
        // (But for now, just creating a new one is fine if we solve the 'Ghost' problem via cleanup)
        // To be safe and clean, let's just always create a new one but pass the OLD id to be replaced/cancelled
        if (BTCPAY_ENABLED && paymentMethod === 'bitcoin') {
          const result = await initiateBtcpayCheckout(
            lines,
            wantsShipping,
            currentOrderId || undefined,
          );
          if (result.success) {
            setCurrentOrderId(result.orderId);
            window.location.href = result.checkoutLink;
          } else {
            setCheckoutError(result.error);
          }
          return;
        }

        const result = await initiatePagaditoCheckout(
          lines,
          wantsShipping,
          currentOrderId || undefined,
        );
        if (result.success) {
          setCurrentOrderId(result.orderId);
          window.location.href = result.redirectUrl;
        } else {
          setCheckoutError(result.error);
        }
      })();
    });
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={`fixed inset-0 bg-black/45 ${getNotificationZIndex()} backdrop-blur-sm`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.aside
            className={`fixed top-0 right-0 h-full w-full max-w-md border-l border-border bg-background shadow-2xl lg:w-[480px] lg:max-w-none ${getCriticalZIndex()} flex flex-col`}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          >
            <header className="flex items-center justify-between border-b border-border px-4 py-6">
              <div className="flex items-center gap-3 text-foreground">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <ShoppingBag className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold tracking-tight">Your Cart</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Close cart drawer"
              >
                <X className="h-6 w-6" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto py-6 px-4 lg:px-6 custom-scrollbar">
              {items.length === 0 ? (
                <div className="py-20 text-center space-y-4">
                  <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                    <ShoppingBag className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground font-medium">Your cart is empty.</p>
                </div>
              ) : (
                <div className="flex flex-col w-full items-start gap-2.5">
                  <div className="order-2 w-full space-y-3 pb-12">
                    {checkoutError && (
                      <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive font-medium" role="alert">
                        {checkoutError}
                      </p>
                    )}

                    <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
                      <button 
                        type="button"
                        onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
                        className="flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Wallet className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-bold text-foreground">Payment Method</p>
                        </div>
                        {isPaymentExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>

                      <AnimatePresence>
                        {isPaymentExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="p-4 pt-0 space-y-3">
                              <div className={`grid gap-2 ${BTCPAY_ENABLED ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                <button
                                  type="button"
                                  onClick={() => setPaymentMethod('pagadito')}
                                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                                    paymentMethod === 'pagadito'
                                      ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                                      : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                                  }`}
                                >
                                  <CreditCard className="h-4 w-4" />
                                  <span>Pagadito</span>
                                </button>
                                {BTCPAY_ENABLED ? (
                                  <button
                                    type="button"
                                    onClick={() => setPaymentMethod('bitcoin')}
                                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                                      paymentMethod === 'bitcoin'
                                        ? 'border-orange-500 bg-orange-500/10 text-orange-300'
                                        : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted'
                                    }`}
                                  >
                                    <Bitcoin className="h-4 w-4" />
                                    <span>Bitcoin</span>
                                  </button>
                                ) : null}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {BTCPAY_ENABLED && paymentMethod === 'bitcoin'
                                  ? 'You will be redirected to BTCPay hosted checkout (Lightning + on-chain).'
                                  : BTCPAY_ENABLED
                                    ? 'You will be redirected to Pagadito hosted checkout.'
                                    : 'You will be redirected to Pagadito hosted checkout. Bitcoin checkout is currently disabled.'}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
                      <button 
                        type="button"
                        onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                        className="flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-bold text-foreground">Shipping</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-primary">
                            {wantsShipping ? formatPrice(pagaditoConfig.shippingFixed) : 'None'}
                          </span>
                          {isShippingExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>

                      <AnimatePresence>
                        {isShippingExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="p-4 pt-0 space-y-2">
                              <button 
                                type="button"
                                onClick={() => setWantsShipping(false)}
                                className={`flex w-full items-center justify-between p-3 rounded-lg border transition-all ${!wantsShipping ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/30'}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`h-4 w-4 rounded-full border flex items-center justify-center ${!wantsShipping ? 'border-primary' : 'border-muted-foreground'}`}>
                                    {!wantsShipping && <div className="h-2 w-2 rounded-full bg-primary" />}
                                  </div>
                                  <span className="text-sm font-bold text-foreground">None</span>
                                </div>
                              </button>

                              <button 
                                type="button"
                                onClick={() => setWantsShipping(true)}
                                className={`flex w-full items-center justify-between p-3 rounded-lg border transition-all ${wantsShipping ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/30'}`}
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <div className={`h-4 w-4 rounded-full border flex items-center justify-center ${wantsShipping ? 'border-primary' : 'border-muted-foreground'}`}>
                                    {wantsShipping && <div className="h-2 w-2 rounded-full bg-primary" />}
                                  </div>
                                  <div className="space-y-0.5">
                                    <p className="text-sm font-bold text-foreground">Costa Rica fixed cost</p>
                                    <p className="text-xs text-muted-foreground">Local delivery service</p>
                                  </div>
                                </div>
                                <span className="text-sm font-bold text-foreground">{formatPrice(pagaditoConfig.shippingFixed)}</span>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="space-y-2.5 rounded-xl border border-border bg-background p-4 shadow-sm text-sm">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Subtotal</span>
                        <span className="font-medium text-foreground">{formatPrice(total)}</span>
                      </div>

                      {shippingCost > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Shipping</span>
                          <span className="font-medium text-foreground">{formatPrice(shippingCost)}</span>
                        </div>
                      )}

                      {transactionFee > 0 && (
                        <div className="flex justify-between text-xs text-muted-foreground italic">
                          <span>Transaction Fee</span>
                          <span>{formatPrice(transactionFee)}</span>
                        </div>
                      )}

                      {cardProcessingFee > 0 && (
                        <div className="flex justify-between text-xs text-muted-foreground italic">
                          <span>Card Processing</span>
                          <span>{formatPrice(cardProcessingFee)}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between border-t border-border pt-4">
                        <span className="text-base font-bold text-foreground">Total</span>
                        <span className="text-xl font-bold text-primary">{formatPrice(finalTotal)}</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleCheckout}
                      className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-cyan-500 to-pink-500 px-6 py-4 text-lg font-bold text-white shadow-lg shadow-cyan-500/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
                      disabled={items.length === 0 || isPending}
                    >
                      {!session.authenticated ? (
                        <UserCircle className="h-6 w-6" />
                      ) : BTCPAY_ENABLED && paymentMethod === 'bitcoin' ? (
                        <Bitcoin className="h-6 w-6" />
                      ) : (
                        <CreditCard className="h-6 w-6" />
                      )}
                      {isPending
                        ? 'Redirecting…'
                        : !session.authenticated
                          ? 'Log in to Checkout'
                          : BTCPAY_ENABLED && paymentMethod === 'bitcoin'
                            ? 'Pay with Bitcoin'
                            : 'Proceed to Checkout'}
                    </button>
                  </div>

                  <div className="order-1 w-full h-fit rounded-xl border border-border bg-muted/20 p-4 mb-2 transition-all">
                    <button 
                      type="button"
                      onClick={() => setIsProductsExpanded(!isProductsExpanded)}
                      className="flex w-full items-center justify-between hover:bg-muted/50 p-1 -m-1 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <PackageCheck className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-bold text-foreground">Products Verification</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {items.length} {items.length === 1 ? 'item' : 'items'}
                        </span>
                        {isProductsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </button>

                    <AnimatePresence>
                      {isProductsExpanded && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="space-y-3 mt-4 pr-1 max-h-[45vh] overflow-y-auto custom-scrollbar">
                            {items.map((item) => (
                              <div key={item.id} className="group relative border border-border bg-background/70 rounded-xl p-3 transition-all hover:bg-background/90 flex gap-3 min-h-[88px]">
                                <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-muted border border-border">
                                  {item.image ? (
                                    <Image src={item.image} alt={item.name} fill className="object-cover" />
                                  ) : (
                                    <div className="h-full w-full bg-muted" />
                                  )}
                                </div>

                                <div className="min-w-0 flex-1 flex flex-col justify-between">
                                  <div className="pr-12">
                                    <h4 className="truncate text-sm font-bold text-foreground">{item.name}</h4>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{formatPrice(item.price)} each</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs font-bold text-primary">
                                      {formatPrice(item.price * item.quantity)}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex flex-col items-end justify-between py-0.5">
                                  <div className="flex items-center gap-1 bg-background border border-border rounded-full p-0.5 scale-90 origin-right">
                                    <button
                                      type="button"
                                      onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                                      className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                      aria-label="Decrease quantity"
                                    >
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="w-6 text-center text-xs font-bold text-foreground">
                                      {item.quantity}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                      className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                      aria-label="Increase quantity"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => removeItem(item.id)}
                                    className="p-1.5 text-destructive hover:bg-destructive/10 rounded-lg transition-all"
                                    aria-label="Remove item"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
